import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faDollarSign, faCrown, faBan, faPhone,
  faImage, faFileAudio, faVideo, faPlus, faEdit, faSpinner,
  faFileAudio as faAudio, faVideo as faVideoIcon, faGem,
  faTrash, faExclamationTriangle, faStickyNote, faTimes,
  faStar, faCircle
} from '@fortawesome/free-solid-svg-icons';
import {
  Card, Form, Button, Table, InputGroup, Alert, Badge, Modal,
  Row, Col
} from '@themesberg/react-bootstrap';
import {
  collection, query, getDocs, orderBy, limit, getDoc, doc,
  addDoc, updateDoc, setDoc, serverTimestamp, onSnapshot,
  Timestamp, where, deleteDoc, increment
} from 'firebase/firestore';
import { uploadToR2, generateUUID } from '../../utils/r2Upload';
import { db, auth, functions } from '../../config/firebase';
import { resolveChatRoomId } from '../../utils/chatUtils';
import { httpsCallable } from 'firebase/functions';

/*
 * ============================================================================
 * ADMIN PANEL MESSAGE REPLY SCREEN - CORE BEHAVIOR
 * ============================================================================
 * 
 * CRITICAL CONCEPT: Admin is NOT an independent chat participant.
 * 
 * Admin ALWAYS acts on behalf of a VERIFIED / APPROVED PROFILE.
 * 
 * In other words:
 * - Admin = operator
 * - Operator ALWAYS sends messages AS a verified profile
 * - Admin identity is ONLY used for permission/access control
 * - Admin uid NEVER appears as senderId, receiverId, or chat participant
 * 
 * URL PARAMETERS:
 * - receiverId: The VERIFIED PROFILE ID (admin operates AS this profile)
 * - senderId: The REAL USER ID who sent the original message
 * 
 * CONVENTION USED IN THIS FILE:
 * - verifiedProfileId = receiverId (the profile admin is operating as)
 * - realUserId = senderId (the actual user on the other end)
 * - adminId = auth.currentUser.uid (ONLY for permission checks, NEVER in chat data)
 * 
 * All chat actions (messages, calls, media) are executed AS the verified profile.
 * ============================================================================
 */

export default () => {
  const { roomId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // URL PARAMETER EXPLANATION:
  // These names come from the perspective of the ORIGINAL message (real user → verified profile)
  const receiverId = queryParams.get('receiverId'); // VERIFIED PROFILE ID (admin operates AS this profile)
  const senderId = queryParams.get('senderId'); // REAL USER ID (the actual user who sent the original message)

  // CLEAR CONCEPT MAPPING:
  // Admin panel messaging is ALWAYS tied to a selected VERIFIED PROFILE.
  // All actions must be executed AS that verified profile.
  const verifiedProfileId = receiverId; // ✅ Admin operates AS this verified profile
  const realUserId = senderId; // ✅ The actual user on the receiving end

  // Admin identity is ONLY for permission/access control, NEVER for chat identity
  const adminId = auth.currentUser?.uid; // ⚠️ ONLY for permission checks, NEVER used as senderId/receiverId

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVerifiedProfileValid, setIsVerifiedProfileValid] = useState(true); // ✅ Onaylı profil validation flag'i
  const [userInfo, setUserInfo] = useState(null); // Mesajı gönderen kullanıcı bilgileri
  const [testUserInfo, setTestUserInfo] = useState(null); // Doğrulanmış profil bilgileri
  const [messages, setMessages] = useState([]); // Sohbet geçmişi
  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);
  const [isBlockedByUser, setIsBlockedByUser] = useState(false); // Engel durumu
  const [userCredits, setUserCredits] = useState(0);
  const [userIsPremium, setUserIsPremium] = useState(false);
  const [userPremiumExpiresAt, setUserPremiumExpiresAt] = useState(null);
  const [userIsBanned, setUserIsBanned] = useState(false);
  const [userIsTestUser, setUserIsTestUser] = useState(false); // Gerçek üye mi kontrolü
  const [testUserCredits, setTestUserCredits] = useState(0);
  const [testUserIsPremium, setTestUserIsPremium] = useState(false);
  const [testUserPremiumExpiresAt, setTestUserPremiumExpiresAt] = useState(null);
  const [testUserIsTestUser, setTestUserIsTestUser] = useState(false); // Fake hesap kontrolü
  const [testUserIsBanned, setTestUserIsBanned] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [callModalCallerId, setCallModalCallerId] = useState(null); // Hangi kullanıcı arayacak (callerId)
  const [callModalReceiverId, setCallModalReceiverId] = useState(null); // Hangi kullanıcı alacak (receiverId)
  const [showQuickMessageModal, setShowQuickMessageModal] = useState(false);
  const [showDiamondModal, setShowDiamondModal] = useState(false);
  const [quickMessages, setQuickMessages] = useState([]);
  const [creditsInput, setCreditsInput] = useState("");
  const [premiumDaysInput, setPremiumDaysInput] = useState("");
  const [callType, setCallType] = useState("normal_voice");
  const [audioRecordings, setAudioRecordings] = useState([]);
  const [videoRecordings, setVideoRecordings] = useState([]);
  const [quickMessageTitle, setQuickMessageTitle] = useState("");
  const [quickMessageContent, setQuickMessageContent] = useState("");
  const [editingQuickMessage, setEditingQuickMessage] = useState(null);
  const [targetUserId, setTargetUserId] = useState(null); // Hangi kullanıcı için işlem yapılacak
  const [appSettings, setAppSettings] = useState(null); // Premium ayarları
  const [coinSettings, setCoinSettings] = useState({
    messageCoin: 1,
    imageCoin: 20,
    videoCoin: 0,
    audioCoin: 0,
    callCoin: 150
  });
  const messageTextareaRef = useRef(null);
  const creditsInputRef = useRef(null);
  const premiumInputRef = useRef(null);
  const [expandedImages, setExpandedImages] = useState(new Set()); // Açık resimlerin ID'leri
  const [newlySentImages, setNewlySentImages] = useState(new Set()); // Yeni gönderilen resimler (ilk gönderimde gösterilsin)
  const [showAllImages, setShowAllImages] = useState(false); // Tüm resimleri göster/gizle (admin panel için)
  const [expandedVideos, setExpandedVideos] = useState(new Set()); // Açık videoların ID'leri
  const [newlySentVideos, setNewlySentVideos] = useState(new Set()); // Yeni gönderilen videolar (ilk gönderimde gösterilsin)
  const [showAllVideos, setShowAllVideos] = useState(false); // Tüm videoları göster/gizle (admin panel için)
  const [conversationNotes, setConversationNotes] = useState([]); // Konuşma notları
  const [noteType, setNoteType] = useState('bot'); // 'bot' veya 'member'
  const [noteContent, setNoteContent] = useState(''); // Manuel not içeriği
  const [noteFilter, setNoteFilter] = useState('all'); // 'all', 'bot', 'member'

  // VIP-Telefon butonu için state'ler
  const [vipPackages, setVipPackages] = useState([]); // VIP paketleri
  const [showVipPhoneModal, setShowVipPhoneModal] = useState(false); // VIP-Telefon modal
  const [vipPhoneMessage, setVipPhoneMessage] = useState('Değerli Kullanıcı telefon numaranızı veya sosyal medya hesabınızı paylaşmak için VIP üye olmalısınız. VIP paket satın almak için tıklayınız.'); // VIP mesaj içeriği
  const [vipPhoneSelectedSku, setVipPhoneSelectedSku] = useState(''); // Seçilen SKU

  // Bildirim modal state'leri
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationBody, setNotificationBody] = useState("");
  const [sendingNotification, setSendingNotification] = useState(false);

  const [quickMessageScale, setQuickMessageScale] = useState(1);
  const [showSystemMessageModal, setShowSystemMessageModal] = useState(false);
  const [systemMessageText, setSystemMessageText] = useState("");
  const [lastSystemMessage, setLastSystemMessage] = useState("");
  const [pastedImage, setPastedImage] = useState(null);
  const [pastedImagePreview, setPastedImagePreview] = useState(null);

  // VIP Şablon state'leri
  const [vipTemplates, setVipTemplates] = useState([]);
  const [showVipTemplateModal, setShowVipTemplateModal] = useState(false);
  const [vipTemplateTitle, setVipTemplateTitle] = useState("");
  const [vipTemplateContent, setVipTemplateContent] = useState("");
  const [editingVipTemplate, setEditingVipTemplate] = useState(null);

  useEffect(() => {
    if (roomId && receiverId && senderId) {
      loadData();
      loadMessages();
      loadAppSettings();
      // Sayfa her yüklendiğinde en başa kaydır (kartların görünmesi için)
      // setTimeout kullanarak layout shiftlerin scroll'u bozmasını engelleriz
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 300);
      // Sayfa yüklendiğinde (F5 yapıldığında) yeni gönderilen resimleri temizle ve resimleri gizle
      setNewlySentImages(new Set());
      setExpandedImages(new Set());
      setShowAllImages(false); // Admin panel: F5 yapıldığında resimler kapalı moda dönmeli
      // Sayfa yüklendiğinde (F5 yapıldığında) yeni gönderilen videoları temizle ve videoları gizle
      setNewlySentVideos(new Set());
      setExpandedVideos(new Set());
      setShowAllVideos(false); // Admin panel: F5 yapıldığında videolar kapalı moda dönmeli
    }
  }, [roomId, receiverId, senderId]);

  // Mesaj textarea'sına otomatik focus
  useEffect(() => {
    if (messageTextareaRef.current) {
      // preventScroll: true sayesinde tarayıcının otomatik aşağı zıplamasını engelleriz
      messageTextareaRef.current.focus({ preventScroll: true });
    }
  }, [roomId]);

  // Modal açıldığında input'a otomatik focus
  useEffect(() => {
    if (showCreditsModal && creditsInputRef.current) {
      setTimeout(() => creditsInputRef.current?.focus(), 100);
    }
  }, [showCreditsModal]);

  useEffect(() => {
    if (showPremiumModal && premiumInputRef.current) {
      setTimeout(() => premiumInputRef.current?.focus(), 100);
    }
  }, [showPremiumModal]);

  // Mesajları dinle (real-time)
  useEffect(() => {
    if (!roomId) return;

    const messagesRef = collection(db, 'chatRooms', roomId, 'messages');
    const q = query(messagesRef, orderBy('timestamp', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messagesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(messagesList);

      // Son sistem mesajını bul ve son sistem mesajı state'ini güncelle
      const systemMsgs = messagesList.filter(m => m.type === 'system' || m.senderId === 'system' || m.isSystemMessage);
      if (systemMsgs.length > 0) {
        setLastSystemMessage(systemMsgs[systemMsgs.length - 1].message);
      }

      // Yeni mesaj geldiğinde aşağı kaydır
      setTimeout(() => {
        const chatHistoryDiv = document.getElementById('chat-history');
        if (chatHistoryDiv) {
          chatHistoryDiv.scrollTo({
            top: chatHistoryDiv.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 100);
    });

    return () => unsubscribe();
  }, [roomId]);

  // İlk yüklemede ve mesaj gönderildiğinde aşağı kaydır (son mesaja)
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => {
        const chatHistoryDiv = document.getElementById('chat-history');
        if (chatHistoryDiv) {
          chatHistoryDiv.scrollTop = chatHistoryDiv.scrollHeight;
        }
      }, 300); // Biraz daha toleranslı süre
    }
  }, [messages.length, roomId]);

  const loadAppSettings = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'appSettings', 'premium'));
      if (settingsDoc.exists()) {
        setAppSettings(settingsDoc.data());
      } else {
        // Varsayılan ayarlar
        setAppSettings({
          requirePremiumToViewImage: true,
          requirePremiumToViewVideo: true,
          requirePremiumToViewAudio: true,
          requirePremiumToSendImage: true,
          requirePremiumToSendVideo: true,
          requirePremiumToSendAudio: true
        });
      }

      // Coin settings yükle
      const coinSettingsDoc = await getDoc(doc(db, 'appSettings', 'coinSettings'));
      if (coinSettingsDoc.exists()) {
        const data = coinSettingsDoc.data();
        setCoinSettings({
          messageCoin: data.messageCoin || 1,
          imageCoin: data.imageCoin || 20,
          videoCoin: data.videoCoin || 0,
          audioCoin: data.voiceCoin || 0,
          callCoin: data.callCoin || 150
        });
      }
    } catch (err) {
      console.error('Error loading app settings:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Mesajı gönderen kullanıcı bilgilerini al
      const senderDoc = await getDoc(doc(db, 'users', senderId));
      const senderData = senderDoc.exists() ? senderDoc.data() : {};
      
      // Kullanıcının bizi (fake profili) engelleyip engellemediğini kontrol et
      const blockedList = senderData.blockedUsers || [];
      if (blockedList.includes(receiverId)) {
        setIsBlockedByUser(true);
      } else {
        setIsBlockedByUser(false);
      }

      setUserInfo({
        id: senderId,
        name: senderData.name || 'Bilinmeyen',
        city: senderData.city || '-',
        age: senderData.age || '-',
        email: senderData.email || null,
        phone: senderData.phone || null,
        profileImageUrl: senderData.profileImageUrl || null,
        credits: senderData.credits || 0,
        isPremium: senderData.isPremium || false,
        premiumExpiresAt: senderData.premiumExpiresAt ? (senderData.premiumExpiresAt.toDate ? senderData.premiumExpiresAt : Timestamp.fromDate(new Date(senderData.premiumExpiresAt))) : null,
        isBanned: senderData.isBanned || false,
        lastSeen: senderData.lastSeen || null
      });
      setUserCredits(senderData.credits || 0);
      setUserIsPremium(senderData.isPremium || false);
      setUserPremiumExpiresAt(senderData.premiumExpiresAt || null);
      setUserIsBanned(senderData.isBanned || false);
      // isTestUser field'ı yok, bu yüzden her zaman false olarak set ediyoruz
      // Tüm kullanıcılar için butonlar gösterilecek
      setUserIsTestUser(false);

      // Doğrulanmış profil bilgilerini al
      const receiverDoc = await getDoc(doc(db, 'users', receiverId));
      const receiverData = receiverDoc.exists() ? receiverDoc.data() : {};
      setTestUserInfo({
        id: receiverId,
        name: receiverData.name || 'Bilinmeyen',
        city: receiverData.city || '-',
        age: receiverData.age || '-',
        email: receiverData.email || null,
        phone: receiverData.phone || null,
        profileImageUrl: receiverData.profileImageUrl || null,
        credits: receiverData.credits || 0,
        isPremium: receiverData.isPremium || false,
        premiumExpiresAt: receiverData.premiumExpiresAt ? (receiverData.premiumExpiresAt.toDate ? receiverData.premiumExpiresAt : Timestamp.fromDate(new Date(receiverData.premiumExpiresAt))) : null,
        lastSeen: receiverData.lastSeen || null
      });
      setTestUserCredits(receiverData.credits || 0);
      setTestUserIsPremium(receiverData.isPremium || false);
      setTestUserPremiumExpiresAt(receiverData.premiumExpiresAt || null);
      // isTestUser field'ı yok, bu yüzden her zaman false olarak set ediyoruz
      setTestUserIsTestUser(false);
      setTestUserIsBanned(receiverData.isBanned || false);

      // Hızlı mesajları yükle
      await loadQuickMessages();

      // Ses/video kayıtlarını yükle (hata durumunda devam et)
      try {
        await loadRecordings();
      } catch (err) {
        console.warn('Recordings load failed, continuing:', err.message);
        setAudioRecordings([]);
        setVideoRecordings([]);
      }

      // Konuşma notlarını yükle
      await loadConversationNotes();

      // VIP paketlerini yükle
      await loadVipPackages();

      // VIP şablonlarını yükle
      await loadVipTemplates();
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Veriler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const messagesRef = collection(db, 'chatRooms', roomId, 'messages');
      const q = query(messagesRef, orderBy('timestamp', 'asc'));
      const snapshot = await getDocs(q);
      const messagesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(messagesList);
    } catch (err) {
      console.error('Error loading messages:', err);
    }
  };

  const loadQuickMessages = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const quickMessagesRef = collection(db, 'users', currentUser.uid, 'quickMessages');
      const snapshot = await getDocs(quickMessagesRef);
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sıralama - küçükten büyüğe
      messages.sort((a, b) => {
        const orderA = a.order !== undefined ? Number(a.order) : 999;
        const orderB = b.order !== undefined ? Number(b.order) : 999;
        return orderA - orderB;
      });

      setQuickMessages(messages);
    } catch (err) {
      console.error('Error loading quick messages:', err);
    }
  };

  const loadVipTemplates = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const templatesRef = collection(db, 'users', currentUser.uid, 'vipTemplates');
      const snapshot = await getDocs(templatesRef);
      const templates = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVipTemplates(templates);
    } catch (err) {
      console.error('Error loading VIP templates:', err);
    }
  };

  const handleSaveVipTemplate = async () => {
    if (!vipTemplateTitle.trim() || !vipTemplateContent.trim()) {
      alert("Lütfen başlık ve içerik girin.");
      return;
    }

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const templatesRef = collection(db, 'users', currentUser.uid, 'vipTemplates');

      if (editingVipTemplate) {
        await updateDoc(doc(db, 'users', currentUser.uid, 'vipTemplates', editingVipTemplate.id), {
          title: vipTemplateTitle,
          content: vipTemplateContent,
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(templatesRef, {
          title: vipTemplateTitle,
          content: vipTemplateContent,
          createdAt: serverTimestamp()
        });
      }

      setShowVipTemplateModal(false);
      setEditingVipTemplate(null);
      setVipTemplateTitle("");
      setVipTemplateContent("");
      loadVipTemplates();
    } catch (err) {
      console.error('Error saving VIP template:', err);
      alert("Hata oluştu.");
    }
  };

  const handleDeleteVipTemplate = async (templateId) => {
    if (!window.confirm("Bu şablonu silmek istediğinize emin misiniz?")) return;

    try {
      const currentUser = auth.currentUser;
      await deleteDoc(doc(db, 'users', currentUser.uid, 'vipTemplates', templateId));
      loadVipTemplates();
    } catch (err) {
      console.error('Error deleting VIP template:', err);
    }
  };

  const loadRecordings = async () => {
    try {
      // Ses/video kayıtları fake hesaptan (receiverId) alınır
      // Çünkü arama fake hesap adına gerçek üyeye gönderilir
      const audioRef = collection(db, 'users', receiverId, 'audioRecordings');
      const audioSnapshot = await getDocs(audioRef);
      const audioList = audioSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAudioRecordings(audioList);

      const videoRef = collection(db, 'users', receiverId, 'videoRecordings');
      const videoSnapshot = await getDocs(videoRef);
      const videoList = videoSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVideoRecordings(videoList);
    } catch (err) {
      // İzin hatası durumunda sessizce geç, sayfa yüklenmesini engelleme
      console.warn('Could not load recordings (permission or data error):', err.message);
      setAudioRecordings([]);
      setVideoRecordings([]);
    }
  };

  // Paketleri yükle (VIP ve Kredi)
  const loadVipPackages = async () => {
    try {
      // VIP paketlerini yükle
      const vipPackagesRef = collection(db, 'vipPackages');
      const snapshotVip = await getDocs(vipPackagesRef);
      const vips = snapshotVip.docs.map(doc => ({
        id: doc.id,
        type: 'VIP',
        ...doc.data()
      }));

      // Kredi paketlerini yükle
      const creditPackagesRef = collection(db, 'creditPackages');
      const snapshotCredit = await getDocs(creditPackagesRef);
      const credits = snapshotCredit.docs.map(doc => ({
        id: doc.id,
        type: 'Kredi',
        ...doc.data()
      }));

      setVipPackages([...vips, ...credits]);
    } catch (err) {
      console.error('Error loading packages:', err);
    }
  };

  const sendMessage = async (text, type = 'text', mediaUrl = null) => {
    if (!text.trim() && !mediaUrl) return;

    if (isBlockedByUser) {
      setError('Bu kullanıcı sizi engellediği için mesaj gönderemezsiniz.');
      alert('Bu hesap sizi (bu profili) engellediği için mesaj atılamaz.');
      return;
    }

    try {
      setSending(true);
      setError(null);

      // ⚠️ ADMIN AUTH CHECK - Permission control ONLY
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError('Admin kullanıcısı bulunamadı.');
        setSending(false);
        return;
      }

      // ✅ CORE CONCEPT: Admin ALWAYS sends messages AS the verified profile
      // Admin uid (currentUser.uid) is NEVER used as senderId in chat data
      // Admin auth is ONLY for permission/access control, NOT for identity

      // ⚠️ IMPORTANT: No credit check from admin panel
      // Admin panel has special privileges, credit check is bypassed

      // ✅ MESSAGE IDENTITY:
      // - senderId = verifiedProfileId (receiverId in URL) - Admin operates AS this profile
      // - receiverId = realUserId (senderId in URL) - The actual user receiving the message
      // - Admin's uid (currentUser.uid) NEVER appears in message data

      // 🔒 KEY RULE: All messages from admin panel appear to come FROM the verified profile
      // Admin is invisible in chat history and message data

      // ChatRoom ID - use from URL or calculate (alphabetical order)
      let targetRoomId = roomId;
      if (!targetRoomId) {
          targetRoomId = await resolveChatRoomId(senderId, receiverId);
      }

      const messagesRef = collection(db, 'chatRooms', targetRoomId, 'messages');
      const messageData = {
        senderId: verifiedProfileId, // ✅ VERIFIED PROFILE sends (admin operates AS this profile)
        receiverId: realUserId, // ✅ REAL USER receives
        message: text || (type === 'image' ? '📷 Fotoğraf' : type === 'video' ? '🎥 Video' : type === 'audio' ? '🎤 Ses' : 'Mesaj'),
        timestamp: serverTimestamp(),
        type: type
      };
      if (mediaUrl) messageData.mediaUrl = mediaUrl;

      // ✅ SEND MESSAGE: Admin identity is NOT included in message data
      await addDoc(messagesRef, messageData);

      // ✅ UPDATE CHATROOM: Admin identity is NOT included in room data
      // The chatRoom only contains the two real participants (verified profile and real user)
      const roomRef = doc(db, 'chatRooms', targetRoomId);
      const roomDoc = await getDoc(roomRef);

      const roomData = {
        participantUids: [realUserId, verifiedProfileId],
        lastMessage: text || (type === 'image' ? '📷 Fotoğraf' : type === 'video' ? '🎥 Video' : type === 'audio' ? '🎤 Ses' : 'Mesaj'),
        lastMessageAt: serverTimestamp(),
        lastMessageType: type,
        lastMessageSenderId: verifiedProfileId,
        ignoredAt: null,
        [`unreadCounts.${realUserId}`]: increment(1)
      };

      if (roomDoc.exists()) {
        await updateDoc(roomRef, roomData);
      } else {
        await setDoc(roomRef, roomData);
      }

      // unansweredMessages collection'ından bu chatRoom için dokümanı sil
      try {
        const unansweredQuery = query(
          collection(db, 'unansweredMessages'),
          where('chatRoomId', '==', targetRoomId)
        );
        const unansweredSnapshot = await getDocs(unansweredQuery);
        await Promise.all(unansweredSnapshot.docs.map(doc => deleteDoc(doc.ref)));
        console.log(`Deleted ${unansweredSnapshot.docs.length} documents from unansweredMessages`);
      } catch (err) {
        console.error('Error deleting from unansweredMessages:', err);
      }

      if (type === 'text') setMessageText("");

      // Eğer resim gönderildiyse, yeni gönderilen resimler listesine ekle (ilk gönderimde gösterilsin)
      if (type === 'image' && mediaUrl) {
        // Mesaj gönderildikten sonra, yeni eklenen mesajı bul ve ID'sini al
        setTimeout(async () => {
          const messagesRef = collection(db, 'chatRooms', targetRoomId, 'messages');
          const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(1));
          const snapshot = await getDocs(q);
          if (!snapshot.empty) {
            const newMsgId = snapshot.docs[0].id;
            setNewlySentImages(prev => new Set(prev).add(newMsgId));
            setExpandedImages(prev => new Set(prev).add(newMsgId)); // İlk gönderimde otomatik açık
          }
        }, 500);
      }

      // Eğer video gönderildiyse, yeni gönderilen videolar listesine ekle (ilk gönderimde gösterilsin)
      if (type === 'video' && mediaUrl) {
        // Mesaj gönderildikten sonra, yeni eklenen mesajı bul ve ID'sini al
        setTimeout(async () => {
          const messagesRef = collection(db, 'chatRooms', targetRoomId, 'messages');
          const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(1));
          const snapshot = await getDocs(q);
          if (!snapshot.empty) {
            const newMsgId = snapshot.docs[0].id;
            setNewlySentVideos(prev => new Set(prev).add(newMsgId));
            setExpandedVideos(prev => new Set(prev).add(newMsgId)); // İlk gönderimde otomatik açık
          }
        }, 500);
      }

      // Mesaj gönderildikten sonra otomatik olarak son mesaja scroll yap
      setTimeout(() => {
        const chatHistoryDiv = document.getElementById('chat-history');
        if (chatHistoryDiv) {
          chatHistoryDiv.scrollTop = chatHistoryDiv.scrollHeight;
        }
      }, 300);
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Mesaj gönderilirken bir hata oluştu: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  const handleSendCustomNotification = async () => {
    if (!notificationTitle.trim() || notificationTitle.trim().length < 5) {
      alert("Bildirim başlığı en az 5 karakter olmalıdır.");
      return;
    }

    try {
      setSendingNotification(true);
      const sendNotificationFn = httpsCallable(functions, 'sendNotification');

      await sendNotificationFn({
        title: notificationTitle,
        message: notificationBody || notificationTitle, // Body boşsa başlığı kullan
        userIds: [realUserId]
      });

      alert("Bildirim başarıyla gönderildi.");
      setShowNotificationModal(false);
      setNotificationTitle("");
      setNotificationBody("");
    } catch (err) {
      console.error('Error sending notification:', err);
      alert("Bildirim gönderilirken bir hata oluştu.");
    } finally {
      setSendingNotification(false);
    }
  };

  const handleSendTextMessage = async () => {
    // Eğer yapıştırılmış resim varsa önce onu gönder
    if (pastedImage) {
      const fileToSend = pastedImage;
      // State'leri temizle
      setPastedImage(null);
      setPastedImagePreview(null);
      // Resmi gönder
      await handleSendImage(fileToSend);
    }

    // Eğer metin de varsa onu gönder
    if (messageText.trim()) {
      await sendMessage(messageText.trim(), 'text');
    }
  };

  const handleSendImage = async (file) => {
    try {
      setSending(true);

      // R2'ye yükle
      const fileName = `chat_media/${roomId}/images/${generateUUID()}.jpg`;
      const downloadUrl = await uploadToR2(file, fileName);
      await sendMessage("Fotoğraf gönderildi", 'image', downloadUrl);
    } catch (err) {
      console.error('Error sending image:', err);
      setError('Fotoğraf gönderilirken bir hata oluştu.');
    } finally {
      setSending(false);
    }
  };

  const handleSendAudio = async (file) => {
    try {
      setSending(true);

      // R2'ye yükle
      const fileName = `chat_media/${roomId}/audio/${generateUUID()}.m4a`;
      const downloadUrl = await uploadToR2(file, fileName);
      await sendMessage("Ses kaydı gönderildi", 'audio', downloadUrl);
    } catch (err) {
      console.error('Error sending audio:', err);
      setError('Ses kaydı gönderilirken bir hata oluştu.');
    } finally {
      setSending(false);
    }
  };

  const handleSendVideo = async (file) => {
    try {
      setSending(true);

      // R2'ye yükle
      const fileName = `chat_media/${roomId}/videos/${generateUUID()}.mp4`;
      const downloadUrl = await uploadToR2(file, fileName);
      await sendMessage("Video gönderildi", 'video', downloadUrl);
    } catch (err) {
      console.error('Error sending video:', err);
      setError('Video gönderilirken bir hata oluştu.');
    } finally {
      setSending(false);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = (event) => {
            setPastedImage(file);
            setPastedImagePreview(event.target.result);
          };
          reader.readAsDataURL(file);
        }
      }
    }
  };

  const handleUpdateCredits = async () => {
    if (!targetUserId) return;

    try {
      const creditsChange = parseInt(creditsInput);
      if (isNaN(creditsChange)) {
        setError('Geçerli bir jeton değeri girin.');
        return;
      }

      const userRef = doc(db, 'users', targetUserId);
      const userDoc = await getDoc(userRef);
      const currentCredits = userDoc.data()?.credits || 0;
      const newCredits = Math.max(0, currentCredits + creditsChange);

      await updateDoc(userRef, {
        credits: newCredits
      });

      if (targetUserId === senderId) {
        setUserCredits(newCredits);
        if (userInfo) setUserInfo({ ...userInfo, credits: newCredits });
      } else {
        setTestUserCredits(newCredits);
        if (testUserInfo) setTestUserInfo({ ...testUserInfo, credits: newCredits });
      }

      setShowCreditsModal(false);
      setCreditsInput("");
      setTargetUserId(null);
    } catch (err) {
      console.error('Error updating credits:', err);
      setError('Jeton güncellenirken bir hata oluştu.');
    }
  };

  const handleSetDiamond = async () => {
    if (!senderId) return;

    try {
      const userRef = doc(db, 'users', senderId);
      await updateDoc(userRef, {
        isDiamond: true
      });

      setShowDiamondModal(false);
    } catch (err) {
      console.error('Error setting diamond:', err);
      setError('Diamond üye yapılırken bir hata oluştu.');
    }
  };

  const handleUpdatePremium = async () => {
    if (!targetUserId) return;

    try {
      const days = parseInt(premiumDaysInput);
      if (isNaN(days) || days < 0) {
        setError('Geçerli bir gün sayısı girin.');
        return;
      }

      const userRef = doc(db, 'users', targetUserId);
      let newExpiresAt = null;

      if (days > 0) {
        const now = new Date();
        const expiresAt = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
        newExpiresAt = Timestamp.fromDate(expiresAt);
      }

      await updateDoc(userRef, {
        isPremium: days > 0,
        premiumExpiresAt: newExpiresAt
      });

      if (targetUserId === senderId) {
        setUserIsPremium(days > 0);
        setUserPremiumExpiresAt(newExpiresAt);
        if (userInfo) setUserInfo({ ...userInfo, isPremium: days > 0, premiumExpiresAt: newExpiresAt });
      } else {
        setTestUserIsPremium(days > 0);
        setTestUserPremiumExpiresAt(newExpiresAt);
        if (testUserInfo) setTestUserInfo({ ...testUserInfo, isPremium: days > 0, premiumExpiresAt: newExpiresAt });
      }

      setShowPremiumModal(false);
      setPremiumDaysInput("");
      setTargetUserId(null);
    } catch (err) {
      console.error('Error updating premium:', err);
      setError('Premium durumu güncellenirken bir hata oluştu.');
    }
  };

  const handleBanUser = async () => {
    if (!targetUserId) return;

    try {
      const userRef = doc(db, 'users', targetUserId);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      const currentBanned = userData?.isBanned || false;
      const isBanning = !currentBanned; // Ban mı kaldırılıyor mu?

      // Kullanıcı bilgilerini güncelle
      await updateDoc(userRef, {
        isBanned: isBanning
      });

      // Cihaz banı - deviceId varsa bannedDevices koleksiyonuna ekle/çıkar
      const deviceId = userData?.deviceId;
      const hardwareId = userData?.hardwareId;
      if (deviceId) {
        const bannedDeviceRef = doc(db, 'bannedDevices', deviceId);
        if (isBanning) {
          // Cihazı banla
          await setDoc(bannedDeviceRef, {
            isBanned: true,
            bannedAt: serverTimestamp(),
            deviceId: deviceId,
            hardwareId: hardwareId || null,
            userId: targetUserId,
            reason: 'Admin panelden banlandı'
          });
          // hardwareId de varsa ayrıca onu da banla
          if (hardwareId) {
            await setDoc(doc(db, 'bannedDevices', hardwareId), {
              isBanned: true,
              bannedAt: serverTimestamp(),
              deviceId: hardwareId,
              hardwareId: hardwareId,
              linkedPrimaryId: deviceId,
              userId: targetUserId,
              type: 'hardware_ban',
              reason: 'Admin panelden banlandı'
            });
          }
          console.log(`Device banned: ${deviceId}, hardware: ${hardwareId} for user: ${targetUserId}`);
        } else {
          // Banı kaldır
          await deleteDoc(bannedDeviceRef);
          if (hardwareId) {
            try { await deleteDoc(doc(db, 'bannedDevices', hardwareId)); } catch (e) { }
          }
          console.log(`Device unbanned: ${deviceId} for user: ${targetUserId}`);
        }
      } else {
        console.warn(`No deviceId found for user ${targetUserId}. User ban applied but device ban skipped.`);
      }

      if (targetUserId === senderId) {
        setUserIsBanned(isBanning);
        if (userInfo) {
          setUserInfo({ ...userInfo, isBanned: isBanning });
        }
      } else {
        setTestUserIsBanned(isBanning);
        if (testUserInfo) {
          setTestUserInfo({ ...testUserInfo, isBanned: isBanning });
        }
      }

      setShowBanModal(false);
      setTargetUserId(null);

      alert(isBanning
        ? 'Kullanıcı ve cihaz başarıyla banlandı. Cihaz artık uygulamaya giriş yapamaz.'
        : 'Kullanıcı ve cihaz banı başarıyla kaldırıldı.');
    } catch (err) {
      console.error('Error banning user:', err);
      setError('Kullanıcı banlanırken bir hata oluştu: ' + err.message);
    }
  };

  const handleStartCall = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError('Admin kullanıcısı bulunamadı.');
        return;
      }

      // ✅ KRİTİK KAVRAM: Admin her zaman DOĞRULANMIŞ PROFİL (receiverId) adına hareket eder
      // Admin bir operatördür, doğrulanmış profilin temsilcisidir
      // Bu yüzden arama her zaman doğrulanmış profilden (receiverId) gerçek üyeye (senderId) yapılır

      const callerId = receiverId; // ✅ Her zaman doğrulanmış profil (fake hesap) arayan
      const receiverIdForCall = senderId; // ✅ Her zaman gerçek üye alıcı

      // Admin panelden arama başlatırken jeton/premium kontrolü yapılmaz
      // Admin her zaman fake hesap adına arama başlatabilir

      // Kayıt kontrolü VE doğrulama
      let mediaUrl = null;
      let callTypeStr = 'voice';

      if (callType === 'normal_voice') {
        // Normal arama - kayıt gerekmez
        mediaUrl = null;
        callTypeStr = 'voice';
      } else if (callType === 'recorded_voice') {
        if (!audioRecordings || audioRecordings.length === 0) {
          setError('Ses kaydı bulunamadı. Lütfen önce ses kaydı ekleyin.');
          return;
        }
        mediaUrl = audioRecordings[0].fileUrl;
        if (!mediaUrl) {
          setError('Ses kaydının URL\'i bulunamadı.');
          return;
        }
        callTypeStr = 'voice';
      } else if (callType === 'recorded_video') {
        if (!videoRecordings || videoRecordings.length === 0) {
          setError('Video kaydı bulunamadı. Lütfen önce video kaydı ekleyin.');
          return;
        }
        mediaUrl = videoRecordings[0].fileUrl;
        if (!mediaUrl) {
          setError('Video kaydının URL\'i bulunamadı.');
          return;
        }
        callTypeStr = 'video';
      }

      // ✅ Admin panelden arama başlatırken:
      // - Doğrulanmış profil (receiverId) her zaman arayan (caller)
      // - Gerçek üye (senderId) her zaman alıcı (receiver)
      // Admin'in kimliği hiçbir zaman arama verisine dahil edilmez

      const callRequestData = {
        callerId: callerId, // ✅ Her zaman doğrulanmış profil (admin bu profili temsil eder)
        receiverId: receiverIdForCall, // ✅ Her zaman gerçek üye
        type: callTypeStr, // 'voice' veya 'video'
        mediaUrl: mediaUrl, // Varsa oynatılır, null ise normal arama
        status: 'ringing',
        createdAt: serverTimestamp()
      };

      console.log('Starting call with data:', callRequestData);

      // callRequests collection'ına ekle
      const callRequestsRef = collection(db, 'callRequests');
      const docRef = await addDoc(callRequestsRef, callRequestData);

      console.log('Call request created successfully with ID:', docRef.id);

      // Başarı mesajı göster
      const callerName = callerId === receiverId ? (testUserInfo?.name || 'Bilinmeyen') : (userInfo?.name || 'Bilinmeyen');
      const receiverName = receiverIdForCall === senderId ? (userInfo?.name || 'Bilinmeyen') : (testUserInfo?.name || 'Bilinmeyen');
      alert(`Arama başarıyla başlatıldı!\n\nArayan: ${callerName}\nAranan: ${receiverName}\nArama Türü: ${callType === 'recorded_video' ? 'Videolu (Kayıtlı)' : callType === 'recorded_voice' ? 'Sesli (Kayıtlı)' : 'Sesli (Normal)'}`);

      setShowCallModal(false);
      setCallModalCallerId(null);
      setCallModalReceiverId(null);
    } catch (err) {
      console.error('Error starting call:', err);
      setError('Arama başlatılırken bir hata oluştu: ' + err.message);
      alert('Hata: ' + err.message + '\n\nDetayları console\'da görebilirsiniz.');
    }
  };

  const handleSaveQuickMessage = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      if (!quickMessageTitle.trim() || !quickMessageContent.trim()) {
        setError('Başlık ve mesaj içeriği gereklidir.');
        return;
      }

      const quickMessagesRef = collection(db, 'users', currentUser.uid, 'quickMessages');

      if (editingQuickMessage) {
        const messageRef = doc(db, 'users', currentUser.uid, 'quickMessages', editingQuickMessage.id);
        await updateDoc(messageRef, {
          title: quickMessageTitle.trim(),
          content: quickMessageContent.trim()
        });
      } else {
        await addDoc(quickMessagesRef, {
          title: quickMessageTitle.trim(),
          content: quickMessageContent.trim(),
          createdAt: serverTimestamp()
        });
      }

      await loadQuickMessages();
      setShowQuickMessageModal(false);
      setQuickMessageTitle("");
      setQuickMessageContent("");
      setEditingQuickMessage(null);
    } catch (err) {
      console.error('Error saving quick message:', err);
      setError('Hızlı mesaj kaydedilirken bir hata oluştu.');
    }
  };

  const getTurkishLocative = (city) => {
    if (!city || city === '-') return '';

    // Temizleme: Şehir adındaki boşlukları ve gereksiz karakterleri temizle
    const cleanCity = city.trim();
    if (!cleanCity) return '';

    const vowels = 'aeıioöuü';
    const lastVowelMatch = cleanCity.match(/[aeıioöuü]/gi);
    const lastVowel = lastVowelMatch ? lastVowelMatch.pop().toLowerCase() : 'a';
    const lastChar = cleanCity.slice(-1).toLowerCase();

    const hardConsonants = 'fstkçşhp';
    const isHard = hardConsonants.includes(lastChar);

    let suffix = '';
    // Kalın ünlüler: a, ı, o, u -> -da / -ta
    if ('aıou'.includes(lastVowel)) {
      suffix = isHard ? "'ta" : "'da";
    }
    // İnce ünlüler: e, i, ö, ü -> -de / -te
    else {
      suffix = isHard ? "'te" : "'de";
    }

    return cleanCity + suffix;
  };

  const replaceSmartVariables = (template) => {
    if (!template) return "";

    let processed = template;

    // Değişken tanımları
    const variables = {
      'uye_sehir_da': getTurkishLocative(userInfo?.city),
      'uye_sehir': userInfo?.city || '',
      'onayli_yas': testUserInfo?.age || '',
      'uye_ad': userInfo?.name || '',
      'onayli_ad': testUserInfo?.name || ''
    };

    Object.keys(variables).forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      // replaceAll güvenli olur ama RegExp ile de g flag'i iş görür
      processed = processed.replace(regex, variables[key]);
    });

    return processed;
  };

  const handleUseQuickMessage = async (quickMsg) => {
    if (!quickMsg.content) return;

    let contentToUse = quickMsg.content;

    // Eğer akıllı mesaj ise değişkenleri değiştir
    if (quickMsg.isSmart) {
      contentToUse = replaceSmartVariables(quickMsg.content);
    }

    // Input alanına yaz (opsiyonel, genelde direkt gönderiliyor ama kullanıcı görsün)
    setMessageText(contentToUse);
    setShowQuickMessageModal(false);

    // Hemen gönder
    try {
      await sendMessage(contentToUse, 'text');
    } catch (err) {
      console.error('Hızlı mesaj gönderilemedi:', err);
    }
  };

  // VIP-Telefon sistem mesajı gönderme fonksiyonu
  const handleSendVipPhoneMessage = async () => {
    if (!vipPhoneMessage.trim()) {
      setError('Mesaj içeriği boş olamaz.');
      return;
    }
    if (!vipPhoneSelectedSku) {
      setError('Lütfen bir VIP paketi seçin.');
      return;
    }

    try {
      setSending(true);
      setError(null);

      // find package type
      const selectedPkg = vipPackages.find(p => p.sku === vipPhoneSelectedSku);
      const packageType = selectedPkg?.type === 'Kredi' ? 'credits' : 'vip';

      let targetRoomId = roomId;
      if (!targetRoomId) {
          targetRoomId = await resolveChatRoomId(senderId, receiverId);
      }
      const messagesRef = collection(db, 'chatRooms', targetRoomId, 'messages');
      const messageData = {
        senderId: 'system',
        receiverId: realUserId,
        message: vipPhoneMessage.trim(),
        timestamp: serverTimestamp(),
        type: 'system',
        sku: vipPhoneSelectedSku,
        packageType: packageType,
        isSystemMessage: true
      };

      await addDoc(messagesRef, messageData);

      const roomRef = doc(db, 'chatRooms', targetRoomId);
      await updateDoc(roomRef, {
        lastMessage: vipPhoneMessage.trim(),
        lastMessageAt: serverTimestamp(),
        lastMessageType: 'system'
      });

      setShowVipPhoneModal(false);
      alert('VIP-Telefon sistem mesajı başarıyla gönderildi.');
    } catch (err) {
      console.error('Error sending VIP phone message:', err);
      setError('Mesaj gönderilirken bir hata oluştu: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  const handleSendOnetapVipPackage = async () => {
    try {
      if (!roomId && (!senderId || !receiverId)) return;
      setError(null);

      const messageText = "İçeriği görüntülemek için VIP Abone olmalısınız. VIP paketleri görüntülemek ve satın almak için tıklayınız.";
      let targetRoomId = roomId;
      if (!targetRoomId) {
          targetRoomId = await resolveChatRoomId(senderId, receiverId);
      }

      const messagesRef = collection(db, 'chatRooms', targetRoomId, 'messages');
      const messageData = {
        senderId: 'system',
        receiverId: realUserId,
        message: messageText,
        timestamp: serverTimestamp(),
        type: 'system',
        packageType: 'vip',
        isSystemMessage: true
      };

      await addDoc(messagesRef, messageData);

      const roomRef = doc(db, 'chatRooms', targetRoomId);
      await updateDoc(roomRef, {
        lastMessage: messageText,
        lastMessageAt: serverTimestamp(),
        lastMessageType: 'system'
      });

    } catch (err) {
      console.error('Error sending One-tap VIP Package message:', err);
      setError('VIP Paket mesajı gönderilemedi.');
    }
  };

  const handleSendOnetapLocationPackage = async () => {
    try {
      if (!roomId && (!senderId || !receiverId)) return;
      setError(null);

      const messageText = "Konum bilgilerini görüntülemek için Konum Paketine sahip olmalısınız. Paketi görüntülemek ve satın almak için tıklayınız.";
      let targetRoomId = roomId;
      if (!targetRoomId) {
          targetRoomId = await resolveChatRoomId(senderId, receiverId);
      }

      const messagesRef = collection(db, 'chatRooms', targetRoomId, 'messages');
      const messageData = {
        senderId: 'system',
        receiverId: realUserId,
        message: messageText,
        timestamp: serverTimestamp(),
        type: 'system',
        packageType: 'location',
        isSystemMessage: true
      };

      await addDoc(messagesRef, messageData);

      const roomRef = doc(db, 'chatRooms', targetRoomId);
      await updateDoc(roomRef, {
        lastMessage: messageText,
        lastMessageAt: serverTimestamp(),
        lastMessageType: 'system'
      });

    } catch (err) {
      console.error('Error sending One-tap Location Package message:', err);
      setError('Konum Paketi mesajı gönderilemedi.');
    }
  };

  // Manuel sistem mesajı gönderme fonksiyonu
  const handleSendManualSystemMessage = async () => {
    if (!systemMessageText.trim()) {
      alert('Lütfen bir mesaj içeriği girin.');
      return;
    }

    try {
      setSending(true);
      setError(null);

      let targetRoomId = roomId;
      if (!targetRoomId) {
          targetRoomId = await resolveChatRoomId(senderId, receiverId);
      }

      // Sistem mesajı olarak gönder
      const messagesRef = collection(db, 'chatRooms', targetRoomId, 'messages');
      const messageData = {
        senderId: 'system',
        receiverId: realUserId,
        message: systemMessageText.trim(),
        timestamp: serverTimestamp(),
        type: 'system',
        isSystemMessage: true
      };

      await addDoc(messagesRef, messageData);

      const roomRef = doc(db, 'chatRooms', targetRoomId);
      await updateDoc(roomRef, {
        lastMessage: systemMessageText.trim(),
        lastMessageAt: serverTimestamp(),
        lastMessageType: 'system'
      });

      setShowSystemMessageModal(false);
      alert('Sistem mesajı başarıyla gönderildi.');
    } catch (err) {
      console.error('Error sending manual system message:', err);
      setError('Mesaj gönderilirken bir hata oluştu: ' + err.message);
    } finally {
      setSending(false);
    }
  };


  // Konuşma notlarını yükle
  const loadConversationNotes = async () => {
    try {
      if (!roomId) return;
      const notesRef = collection(db, 'chatRooms', roomId, 'notes');
      const snapshot = await getDocs(notesRef);
      const notesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setConversationNotes(notesList.sort((a, b) => {
        const aTime = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const bTime = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return bTime - aTime; // Yeni notlar üstte
      }));
    } catch (err) {
      console.error('Error loading conversation notes:', err);
    }
  };

  // Mesajı sil
  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Bu mesajı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.')) {
      return;
    }
    try {
      const messageRef = doc(db, 'chatRooms', roomId, 'messages', messageId);
      await deleteDoc(messageRef);
      // Mesaj listesini güncelle
      setMessages(messages.filter(msg => msg.id !== messageId));
      alert('Mesaj başarıyla silindi.');
    } catch (err) {
      console.error('Error deleting message:', err);
      alert('Mesaj silinirken bir hata oluştu: ' + err.message);
    }
  };

  // Mesajı sansürle
  const handleCensorMessage = async (messageId, messageText) => {
    if (!window.confirm('Bu mesajı sansürlemek istediğinize emin misiniz?')) {
      return;
    }
    try {
      const censoredText = '*'.repeat(messageText.length);
      const messageRef = doc(db, 'chatRooms', roomId, 'messages', messageId);
      await updateDoc(messageRef, {
        message: censoredText
      });
      // Mesaj listesini güncelle
      setMessages(messages.map(msg =>
        msg.id === messageId ? { ...msg, message: censoredText } : msg
      ));
      alert('Mesaj başarıyla sansürlendi.');
    } catch (err) {
      console.error('Error censoring message:', err);
      alert('Mesaj sansürlenirken bir hata oluştu: ' + err.message);
    }
  };

  // Mesajdan not ekle
  const handleAddNoteFromMessage = async (message, senderId) => {
    try {
      if (!roomId) return;
      const isFromApprovedProfile = senderId === receiverId;
      const noteTypeToUse = isFromApprovedProfile ? 'bot' : 'member';

      // Mesaj içeriğini al
      let messageContent = '';
      if (typeof message === 'string') {
        messageContent = message;
      } else if (message && message.message) {
        messageContent = message.message;
      } else {
        messageContent = 'Mesaj içeriği yok';
      }

      const notesRef = collection(db, 'chatRooms', roomId, 'notes');
      await addDoc(notesRef, {
        type: noteTypeToUse,
        content: messageContent,
        createdAt: serverTimestamp()
      });

      await loadConversationNotes();
      alert('Not başarıyla eklendi.');
    } catch (err) {
      console.error('Error adding note from message:', err);
      alert('Not eklenirken bir hata oluştu: ' + err.message);
    }
  };

  // Manuel not ekle
  const handleAddManualNote = async () => {
    try {
      if (!noteContent.trim()) {
        alert('Lütfen not içeriği girin.');
        return;
      }
      if (!roomId) return;

      const notesRef = collection(db, 'chatRooms', roomId, 'notes');
      await addDoc(notesRef, {
        type: noteType,
        content: noteContent.trim(),
        createdAt: serverTimestamp()
      });

      await loadConversationNotes();
      setNoteContent('');
      alert('Not başarıyla eklendi.');
    } catch (err) {
      console.error('Error adding manual note:', err);
      alert('Not eklenirken bir hata oluştu: ' + err.message);
    }
  };

  // Notu sil
  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Bu notu silmek istediğinize emin misiniz?')) {
      return;
    }
    try {
      const noteRef = doc(db, 'chatRooms', roomId, 'notes', noteId);
      await deleteDoc(noteRef);
      await loadConversationNotes();
      alert('Not başarıyla silindi.');
    } catch (err) {
      console.error('Error deleting note:', err);
      alert('Not silinirken bir hata oluştu: ' + err.message);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    // Türkiye saat dilimine göre göster (Europe/Istanbul)
    return date.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Istanbul'
    });
  };

  const canViewMedia = (messageType) => {
    // Admin panelde VIP kontrolü yapılmaz, tüm içerikler görüntülenebilir
    return true;
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <FontAwesomeIcon icon={faSpinner} className="fa-spin fa-2x" />
        <p className="mt-3">Yükleniyor...</p>
      </div>
    );
  }

  if (!userInfo || !testUserInfo) {
    return (
      <Alert variant="danger">
        Kullanıcı bilgileri yüklenemedi.
      </Alert>
    );
  }

  return (
    <>
      <div className="py-4">
        <div className="d-flex align-items-center mb-4">
          <Button variant="secondary" onClick={() => history.goBack()} className="me-3">
            <FontAwesomeIcon icon={faArrowLeft} /> Geri
          </Button>
          <h4>Mesaj Cevap</h4>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Row>
        {/* Sol Taraf: Sohbet Geçmişi ve Mesaj Yazma */}
        <Col md={8}>
          {/* Sohbet Geçmişi */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Sohbet Geçmişi</h5>
                <div className="d-flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleSendOnetapVipPackage}
                  >
                    <FontAwesomeIcon icon={faCrown} className="me-1" />
                    VIP Paket
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleSendOnetapLocationPackage}
                  >
                    <FontAwesomeIcon icon={faCrown} className="me-1" />
                    Konum Paket
                  </Button>
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={() => setShowVipPhoneModal(true)}
                  >
                    <FontAwesomeIcon icon={faStar} className="me-1" />
                    VIP-Telefon
                  </Button>
                  <Button
                    variant="info"
                    size="sm"
                    onClick={() => {
                      setSystemMessageText(lastSystemMessage);
                      setShowSystemMessageModal(true);
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    Sistem Mesajı
                  </Button>
                </div>
              </div>
              {messages.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">Henüz mesaj yok.</p>
                </div>
              ) : (
                <div
                  id="chat-history"
                  style={{
                    maxHeight: '70vh',
                    minHeight: '500px',
                    overflowY: 'auto',
                    padding: '10px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px'
                  }}
                >
                  {/* Admin panel: Tüm resimleri göster/gizle butonu - sadece resim mesajları varsa göster */}
                  {messages.some(m => m.type === 'image' && canViewMedia(m.type)) && (
                    <div className="mb-3 d-flex justify-content-center">
                      <Button
                        variant={showAllImages ? "danger" : "primary"}
                        size="sm"
                        onClick={() => {
                          setShowAllImages(!showAllImages);
                        }}
                      >
                        <FontAwesomeIcon icon={faImage} className="me-2" />
                        {showAllImages ? 'Resim Gizle' : 'Resim Göster'}
                      </Button>
                    </div>
                  )}
                  {/* Admin panel: Tüm videoları göster/gizle butonu - sadece video mesajları varsa göster */}
                  {messages.some(m => m.type === 'video' && canViewMedia(m.type)) && (
                    <div className="mb-3 d-flex justify-content-center">
                      <Button
                        variant={showAllVideos ? "danger" : "primary"}
                        size="sm"
                        onClick={() => {
                          setShowAllVideos(!showAllVideos);
                        }}
                      >
                        <FontAwesomeIcon icon={faVideoIcon} className="me-2" />
                        {showAllVideos ? 'Videoyu Gizle' : 'Videoyu Göster'}
                      </Button>
                    </div>
                  )}

                  {messages.map((msg, index) => {
                    const isFromTestUser = msg.senderId === receiverId;
                    const isSystemMessage = msg.type === 'system' || msg.senderId === 'system' || msg.isSystemMessage;
                    const canView = canViewMedia(msg.type);
                    const isFirstImage = msg.type === 'image' && index === messages.findIndex(m => m.type === 'image' && canViewMedia(m.type));
                    const isFirstVideo = msg.type === 'video' && index === messages.findIndex(m => m.type === 'video' && canViewMedia(m.type));
                    const senderInfo = isFromTestUser ? testUserInfo : userInfo;
                    const senderName = senderInfo?.name || 'Bilinmeyen';
                    const senderAvatar = senderInfo?.profileImageUrl || null;

                    // Sistem mesajı için özel görünüm - ortada gösterilir
                    if (isSystemMessage) {
                      return (
                        <div key={msg.id} className="mb-3 d-flex justify-content-center">
                          <div
                            className="p-3 rounded position-relative"
                            style={{
                              maxWidth: '70%',
                              backgroundColor: '#FFF3E0',
                              border: '2px solid #FFB300',
                              boxShadow: '0 2px 8px rgba(255, 179, 0, 0.3)'
                            }}
                          >
                            {/* Sistem mesajı etiketi */}
                            <div className="d-flex align-items-center justify-content-center mb-2">
                              <FontAwesomeIcon icon={faStar} className="text-warning me-2" />
                              <span className="fw-bold text-warning" style={{ fontSize: '12px' }}>
                                Sistem Mesajı
                              </span>
                              <small className="ms-2 text-muted" style={{ fontSize: '11px' }}>
                                {formatDate(msg.timestamp)}
                              </small>
                            </div>

                            {/* VIP ikonu */}
                            <div className="text-center mb-2">
                              <FontAwesomeIcon icon={faStar} className="text-warning" style={{ fontSize: '24px' }} />
                            </div>

                            {/* Mesaj içeriği */}
                            <div
                              className="text-center mb-2"
                              style={{
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                color: '#1F1F1F'
                              }}
                            >
                              {msg.message}
                            </div>

                            {/* CTA butonu */}
                            <div
                              className="text-center p-2 rounded"
                              style={{
                                backgroundColor: '#EA5BD2',
                                color: 'white',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                              }}
                            >
                              VIP Paketini İncele
                            </div>

                            {/* Admin butonları */}
                            <div className="d-flex justify-content-center gap-2 mt-2">
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0"
                                onClick={() => handleDeleteMessage(msg.id)}
                                title="Mesajı Sil"
                                style={{
                                  color: '#dc3545',
                                  textDecoration: 'none',
                                  padding: '2px 4px'
                                }}
                              >
                                <FontAwesomeIcon icon={faTrash} size="sm" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={msg.id} className={`mb-3 d-flex flex-column ${isFromTestUser ? 'align-items-end' : 'align-items-start'}`}>
                        {/* ✅ BUTONLAR - Mesaj balonunun DIŞINDA, üstte */}
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            onClick={() => handleAddNoteFromMessage(msg, msg.senderId)}
                            title="Nota Ekle"
                            style={{
                              color: '#6c757d',
                              textDecoration: 'none',
                              padding: '2px 4px'
                            }}
                          >
                            <FontAwesomeIcon icon={faStickyNote} size="sm" />
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            onClick={() => {
                              if (msg.type === 'text' && msg.message) {
                                handleCensorMessage(msg.id, msg.message);
                              } else {
                                alert('Sidesce metin mesajları sansürlenebilir.');
                              }
                            }}
                            title="Sansürle"
                            style={{
                              color: '#6c757d',
                              textDecoration: 'none',
                              padding: '2px 4px'
                            }}
                          >
                            <FontAwesomeIcon icon={faExclamationTriangle} size="sm" />
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            onClick={() => handleDeleteMessage(msg.id)}
                            title="Mesajı Sil"
                            style={{
                              color: '#dc3545',
                              textDecoration: 'none',
                              padding: '2px 4px'
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} size="sm" />
                          </Button>
                        </div>

                        {/* Mesaj balonu */}
                        <div className={`p-3 rounded position-relative ${isFromTestUser ? 'bg-primary text-white' : 'bg-light'}`} style={{ maxWidth: '70%' }}>
                          {/* Mesaj başlığı: Gönderen adı ve profil fotoğrafı */}
                          <div className="d-flex align-items-center mb-2">
                            {senderAvatar && (
                              <img
                                src={senderAvatar}
                                alt={senderName}
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  marginRight: '8px',
                                  objectFit: 'cover'
                                }}
                              />
                            )}
                            <span className={`fw-bold ${isFromTestUser ? 'text-white' : 'text-dark'}`} style={{ fontSize: '14px' }}>
                              {senderName}
                            </span>
                            <small className={`ms-2 ${isFromTestUser ? 'text-white-50' : 'text-muted'}`} style={{ fontSize: '11px' }}>
                              {formatDate(msg.timestamp)}
                            </small>
                          </div>

                          {msg.type === 'text' && (
                            <div style={{
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}>
                              {msg.message}
                            </div>
                          )}
                          {msg.type === 'image' && (
                            <React.Fragment>
                              {/* ✅ Resim içeriği */}
                              {showAllImages || expandedImages.has(msg.id) || newlySentImages.has(msg.id) ? (
                                <div>
                                  {/* Bireysel resim gizle butonu */}
                                  <div className="mb-2">
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      onClick={() => {
                                        // Hem genel görünümü hem de bu resmin özel görünümünü kapat
                                        setShowAllImages(false);
                                        setExpandedImages(prev => {
                                          const newSet = new Set(prev);
                                          newSet.delete(msg.id);
                                          return newSet;
                                        });
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faImage} className="me-2" />
                                      Resim Gizle
                                    </Button>
                                  </div>
                                  <img
                                    src={msg.mediaUrl}
                                    alt="Fotoğraf"
                                    style={{
                                      maxWidth: '150px',
                                      maxHeight: '150px',
                                      width: '150px',
                                      height: '150px',
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                      objectFit: 'cover',
                                      border: '2px solid #ddd'
                                    }}
                                  />
                                  <div className="mt-1" style={{
                                    wordWrap: 'break-word',
                                    overflowWrap: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}>{msg.message}</div>
                                </div>
                              ) : (
                                <div
                                  style={{
                                    padding: '8px 12px',
                                    backgroundColor: isFromTestUser ? 'rgba(255,255,255,0.2)' : '#e9ecef',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    display: 'inline-block'
                                  }}
                                  onClick={() => {
                                    setExpandedImages(prev => {
                                      const newSet = new Set(prev);
                                      newSet.add(msg.id);
                                      return newSet;
                                    });
                                  }}
                                >
                                  <FontAwesomeIcon icon={faImage} className="me-2" />
                                  Resim Göster
                                </div>
                              )}
                            </React.Fragment>
                          )}
                          {msg.type === 'video' && (
                            <React.Fragment>
                              {showAllVideos || expandedVideos.has(msg.id) || newlySentVideos.has(msg.id) ? (
                                <div>
                                  {isFirstVideo && showAllVideos && (
                                    <div className="mb-2">
                                      <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={() => {
                                          setShowAllVideos(false);
                                        }}
                                      >
                                        <FontAwesomeIcon icon={faVideoIcon} className="me-2" />
                                        Videoyu Gizle
                                      </Button>
                                    </div>
                                  )}
                                  <video
                                    src={msg.mediaUrl}
                                    controls
                                    style={{
                                      width: '150px',
                                      height: '150px',
                                      borderRadius: '8px',
                                      objectFit: 'cover',
                                      border: '2px solid #ddd'
                                    }}
                                  />
                                  <div className="mt-1" style={{
                                    wordWrap: 'break-word',
                                    overflowWrap: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}>{msg.message}</div>
                                </div>
                              ) : (
                                <div
                                  style={{
                                    padding: '8px 12px',
                                    backgroundColor: isFromTestUser ? 'rgba(255,255,255,0.2)' : '#e9ecef',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    display: 'inline-block'
                                  }}
                                  onClick={() => {
                                    setShowAllVideos(true);
                                  }}
                                >
                                  <FontAwesomeIcon icon={faVideoIcon} className="me-2" />
                                  Videoyu Göster
                                </div>
                              )}
                            </React.Fragment>
                          )}
                          {msg.type === 'call_log' && (
                            <div className="d-flex align-items-center gap-2">
                              {msg.message.toLowerCase().includes('sesli') ? (
                                <FontAwesomeIcon icon={faPhone} className={isFromTestUser ? 'text-white-50' : 'text-muted'} />
                              ) : (
                                <FontAwesomeIcon icon={faVideoIcon} className={isFromTestUser ? 'text-white-50' : 'text-muted'} />
                              )}
                              <div style={{
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontStyle: 'italic'
                              }}>
                                {msg.message}
                              </div>
                            </div>
                          )}
                          {msg.type === 'audio' && (
                            <div>
                              <audio controls style={{ width: '100%' }} crossOrigin="anonymous" preload="metadata">
                                <source src={msg.mediaUrl} type="audio/mp4" />
                                <source src={msg.mediaUrl} type="audio/mpeg" />
                                <source src={msg.mediaUrl} type="audio/m4a" />
                              </audio>
                              <div className="mt-1 d-flex align-items-center gap-2" style={{
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                              }}>
                                {msg.message}
                                <a href={msg.mediaUrl} target="_blank" rel="noopener noreferrer" className="text-primary small">
                                  ⬇ İndir
                                </a>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Mesaj Yazma Alanı */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
              <h5 className="mb-3">Mesaj Gönder</h5>

              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label mb-0">Hızlı Mesajlar</label>
                  <div className="d-flex gap-2">
                    {/* Düzenleme Butonu - Hızlı Mesajlar Sayfasına Yönlendirir */}
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={() => history.push('/messages/quick-messages')}
                    >
                      <FontAwesomeIcon icon={faEdit} className="me-1" />
                      Düzenle
                    </Button>
                    {/* Artı Butonu - Yeni Mesaj Ekleme Modal'ı Açar */}
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => {
                        setEditingQuickMessage(null);
                        setQuickMessageTitle("");
                        setQuickMessageContent("");
                        setShowQuickMessageModal(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-1" />
                      Yeni
                    </Button>
                  </div>
                </div>
                {quickMessages.length > 0 && (
                  <div className="d-flex justify-content-between align-items-center mb-2 mt-2">
                    <span className="text-muted small fw-bold">Hızlı Mesajlar Boyutu</span>
                    <Form.Range
                      min={0.5}
                      max={2.0}
                      step={0.1}
                      value={quickMessageScale}
                      onChange={(e) => setQuickMessageScale(parseFloat(e.target.value))}
                      style={{ width: '150px' }}
                    />
                    <span className="small text-muted">{Math.round(quickMessageScale * 100)}%</span>
                  </div>
                )}
                {quickMessages.length > 0 ? (
                  <div
                    className="d-flex flex-wrap"
                    style={{
                      gap: `${0.5 * quickMessageScale}rem`,
                      marginBottom: '1rem'
                    }}
                  >
                    {quickMessages.map((msg) => (
                      <Badge
                        key={msg.id}
                        bg={msg.isSmart ? "warning" : "primary"}
                        text={msg.isSmart ? "dark" : "white"}
                        className="cursor-pointer"
                        onClick={() => handleUseQuickMessage(msg)}
                        style={{
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          fontSize: `${110 * quickMessageScale}%`,
                          padding: `${10 * quickMessageScale}px ${15 * quickMessageScale}px`,
                          textTransform: 'uppercase'
                        }}
                      >
                        {msg.isSmart && <FontAwesomeIcon icon={faStar} className="me-1" />}
                        {msg.title}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <Alert variant="info" className="mb-0">
                    <small>Henüz hızlı mesaj yok. Sağ üstteki "Yeni" butonuna tıklayarak ekleyebilirsiniz.</small>
                  </Alert>
                )}
              </div>

              {pastedImagePreview && (
                <div className="position-relative mb-2" style={{ width: 'fit-content' }}>
                  <img
                    src={pastedImagePreview}
                    alt="Pasted preview"
                    style={{ maxHeight: '150px', borderRadius: '8px', border: '1px solid #ddd' }}
                  />
                  <Button
                    variant="danger"
                    size="sm"
                    className="position-absolute top-0 end-0 rounded-circle p-0 d-flex align-items-center justify-content-center"
                    style={{ transform: 'translate(50%, -50%)', width: '24px', height: '24px' }}
                    onClick={() => {
                      setPastedImage(null);
                      setPastedImagePreview(null);
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} style={{ fontSize: '12px' }} />
                  </Button>
                </div>
              )}

              <Form.Group className="mb-3">
                <Form.Control
                  ref={messageTextareaRef}
                  as="textarea"
                  rows={3}
                  placeholder={isBlockedByUser ? "Bu hesap sizi engellediği için mesaj yazamazsınız." : "Mesaj Yaz"}
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onPaste={handlePaste}
                  disabled={isBlockedByUser}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (!isBlockedByUser) handleSendTextMessage();
                    }
                  }}
                  autoFocus={!isBlockedByUser}
                />
              </Form.Group>

              <div className="d-flex gap-2 mb-3">
                <InputGroup>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleSendImage(file);
                    }}
                    style={{ display: 'none' }}
                    id="imageInput"
                  />
                  <Button
                    variant="outline-primary"
                    onClick={() => document.getElementById('imageInput').click()}
                    disabled={sending}
                  >
                    <FontAwesomeIcon icon={faImage} /> Fotoğraf
                  </Button>
                </InputGroup>

                <InputGroup>
                  <Form.Control
                    type="file"
                    accept="audio/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleSendAudio(file);
                    }}
                    style={{ display: 'none' }}
                    id="audioInput"
                  />
                  <Button
                    variant="outline-primary"
                    onClick={() => document.getElementById('audioInput').click()}
                    disabled={sending}
                  >
                    <FontAwesomeIcon icon={faFileAudio} /> Ses
                  </Button>
                </InputGroup>

                <InputGroup>
                  <Form.Control
                    type="file"
                    accept="video/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleSendVideo(file);
                    }}
                    style={{ display: 'none' }}
                    id="videoInput"
                  />
                  <Button
                    variant="outline-primary"
                    onClick={() => document.getElementById('videoInput').click()}
                    disabled={sending}
                  >
                    <FontAwesomeIcon icon={faVideo} /> Video
                  </Button>
                </InputGroup>
              </div>

              <Button
                variant="primary"
                onClick={handleSendTextMessage}
                disabled={(!messageText.trim() && !pastedImage) || sending}
                className="w-100"
              >
                {sending ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="fa-spin me-2" />
                    Gönderiliyor...
                  </>
                ) : (
                  'Gönder'
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>

        {/* Sağ Taraf: İki Ayrı Kare - Fake Hesap ve Konuşulan Üye */}
        <Col md={4}>
          {/* Fake Hesap (Doğrulanmış Profil) */}
          <Card className="border-0 shadow-sm mb-4" style={{ fontSize: '0.85rem' }}>
            <Card.Body>
              <Row className="mb-3 align-items-center">
                <Col xs={7}>
                  <div className="d-grid gap-1">
                    <Button
                      variant="outline-success"
                      size="sm"
                      className="py-1 px-2 text-start"
                      onClick={() => {
                        setCreditsInput("");
                        setTargetUserId(receiverId);
                        setShowCreditsModal(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faDollarSign} className="me-2" /> Jeton Ekle/Sil
                    </Button>

                    <Button
                      variant="outline-warning"
                      size="sm"
                      className="py-1 px-2 text-start"
                      onClick={() => {
                        setPremiumDaysInput("");
                        setTargetUserId(receiverId);
                        setShowPremiumModal(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faCrown} className="me-2" /> Premium Yap/Kaldır
                    </Button>

                    <Button
                      variant="outline-info"
                      size="sm"
                      className="py-1 px-2 text-start"
                      onClick={async () => {
                        try {
                          const userRef = doc(db, 'users', receiverId);
                          await updateDoc(userRef, { isDiamond: true });
                          alert('Diamond üye yapıldı!');
                          await loadData();
                        } catch (err) {
                          console.error('Error setting diamond:', err);
                          setError('Diamond üye yapılırken bir hata oluştu.');
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faGem} className="me-2" /> Diamond Üye Yap
                    </Button>

                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="py-1 px-2 text-start"
                      onClick={() => {
                        setTargetUserId(receiverId);
                        setShowBanModal(true);
                      }}
                      disabled={testUserIsBanned}
                    >
                      <FontAwesomeIcon icon={faBan} className="me-2" /> {testUserIsBanned ? 'Banlı' : 'Banla'}
                    </Button>

                    <div className="d-flex gap-1 mt-1">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="py-0 px-2 flex-grow-1"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => history.push(`/accounts/recordings/${receiverId}?type=audio`)}
                      >
                        <FontAwesomeIcon icon={faFileAudio} className="me-1" /> Ses ({audioRecordings.length})
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="py-0 px-2 flex-grow-1"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => history.push(`/accounts/recordings/${receiverId}?type=video`)}
                      >
                        <FontAwesomeIcon icon={faVideo} className="me-1" /> Video ({videoRecordings.length})
                      </Button>
                    </div>
                  </div>
                </Col>
                <Col xs={5} className="text-center d-flex flex-column align-items-center justify-content-center">
                  {testUserInfo.profileImageUrl ? (
                    <img
                      src={testUserInfo.profileImageUrl}
                      alt={testUserInfo.name}
                      className="rounded-circle shadow-sm"
                      style={{ width: 70, height: 70, objectFit: 'cover', border: '2px solid #eee' }}
                    />
                  ) : (
                    <div className="rounded-circle bg-light d-flex align-items-center justify-content-center shadow-sm" style={{ width: 70, height: 70, fontSize: '1.5rem', border: '2px solid #eee' }}>
                      {testUserInfo.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="mt-2 fw-bold text-dark" style={{ fontSize: '1rem' }}>{testUserInfo.name}</div>
                </Col>
              </Row>

              <Table className="table-sm mb-0 custom-profile-table">
                <tbody>
                  <tr>
                    <td className="text-muted" style={{ width: '30%' }}>Durum:</td>
                    <td>
                      {(() => {
                        const lastSeen = testUserInfo?.lastSeen;
                        const isOnline = lastSeen && (Date.now() - (lastSeen.toDate ? lastSeen.toDate() : new Date(lastSeen)).getTime() < 5 * 60 * 1000);
                        return isOnline ? (
                          <span className="text-success fw-bold">
                            <FontAwesomeIcon icon={faCircle} className="me-1 small" style={{ fontSize: '0.6rem' }} /> Online
                          </span>
                        ) : (
                          <span className="text-muted">
                            {lastSeen ? (lastSeen.toDate ? lastSeen.toDate() : new Date(lastSeen)).toLocaleString('tr-TR') : 'Bilinmiyor'}
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted" style={{ width: '30%' }}>Şehir:</td>
                    <td>{testUserInfo.city}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Yaş:</td>
                    <td>{testUserInfo.age}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Jeton:</td>
                    <td><Badge bg="success" className="fw-normal">{testUserCredits}</Badge></td>
                  </tr>
                  <tr>
                    <td className="text-muted">Premium:</td>
                    <td>
                      {testUserIsPremium ? (
                        <span className="text-warning">
                          <FontAwesomeIcon icon={faCrown} className="me-1" />
                          {testUserPremiumExpiresAt ?
                            (testUserPremiumExpiresAt.toDate ? testUserPremiumExpiresAt.toDate().toLocaleDateString('tr-TR') : new Date(testUserPremiumExpiresAt).toLocaleDateString('tr-TR')) :
                            'Aktif'}
                        </span>
                      ) : (
                        'Hayır'
                      )}
                    </td>
                  </tr>
                  {testUserInfo.email && (
                    <tr>
                      <td className="text-muted">Email:</td>
                      <td className="small text-truncate" style={{ maxWidth: '150px' }}>{testUserInfo.email}</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Konuşulan Üye (Mesajı Gönderen Kullanıcı) */}
          <Card className="border-0 shadow-sm mb-4" style={{ fontSize: '0.85rem' }}>
            <Card.Body>
              <Row className="mb-3 align-items-center">
                <Col xs={7}>
                  <div className="d-grid gap-1">
                    <Button
                      variant="outline-success"
                      size="sm"
                      className="py-1 px-2 text-start"
                      onClick={() => {
                        setCreditsInput("");
                        setTargetUserId(senderId);
                        setShowCreditsModal(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faDollarSign} className="me-2" /> Jeton Ekle/Sil
                    </Button>

                    <Button
                      variant="outline-warning"
                      size="sm"
                      className="py-1 px-2 text-start"
                      onClick={() => {
                        setPremiumDaysInput("");
                        setTargetUserId(senderId);
                        setShowPremiumModal(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faCrown} className="me-2" /> Premium Yap/Kaldır
                    </Button>

                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="py-1 px-2 text-start"
                      onClick={() => {
                        setCallModalCallerId(receiverId);
                        setCallModalReceiverId(senderId);
                        setShowCallModal(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faPhone} className="me-2" /> Arama Başlat
                    </Button>

                    <Button
                      variant="outline-info"
                      size="sm"
                      className="py-1 px-2 text-start"
                      onClick={() => setShowDiamondModal(true)}
                    >
                      <FontAwesomeIcon icon={faGem} className="me-2" /> Diamond Üye Yap
                    </Button>

                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="py-1 px-2 text-start"
                      onClick={() => setShowBanModal(true)}
                      disabled={userIsBanned}
                    >
                      <FontAwesomeIcon icon={faBan} className="me-2" /> {userIsBanned ? 'Banlı' : 'Banla'}
                    </Button>

                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="py-1 px-2 text-start"
                      style={{ borderStyle: 'dashed' }}
                      onClick={() => setShowNotificationModal(true)}
                    >
                      <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" /> Bildirim Gönder
                    </Button>
                  </div>
                </Col>
                <Col xs={5} className="text-center d-flex flex-column align-items-center justify-content-center">
                  {userInfo.profileImageUrl ? (
                    <img
                      src={userInfo.profileImageUrl}
                      alt={userInfo.name}
                      className="rounded-circle shadow-sm"
                      style={{ width: 70, height: 70, objectFit: 'cover', border: '2px solid #eee' }}
                    />
                  ) : (
                    <div className="rounded-circle bg-light d-flex align-items-center justify-content-center shadow-sm" style={{ width: 70, height: 70, fontSize: '1.5rem', border: '2px solid #eee' }}>
                      {userInfo.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="mt-2 fw-bold text-dark" style={{ fontSize: '1rem' }}>{userInfo.name}</div>
                </Col>
              </Row>

              <Table className="table-sm mb-0 custom-profile-table">
                <tbody>
                  <tr>
                    <td className="text-muted" style={{ width: '30%' }}>Durum:</td>
                    <td>
                      {(() => {
                        const lastSeen = userInfo?.lastSeen;
                        const isOnline = lastSeen && (Date.now() - (lastSeen.toDate ? lastSeen.toDate() : new Date(lastSeen)).getTime() < 5 * 60 * 1000);
                        return isOnline ? (
                          <span className="text-success fw-bold">
                            <FontAwesomeIcon icon={faCircle} className="me-1 small" style={{ fontSize: '0.6rem' }} /> Online
                          </span>
                        ) : (
                          <span className="text-muted">
                            {lastSeen ? (lastSeen.toDate ? lastSeen.toDate() : new Date(lastSeen)).toLocaleString('tr-TR') : 'Bilinmiyor'}
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted" style={{ width: '30%' }}>Şehir:</td>
                    <td>{userInfo.city}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Yaş:</td>
                    <td>{userInfo.age}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Jeton:</td>
                    <td><Badge bg="success" className="fw-normal">{userCredits}</Badge></td>
                  </tr>
                  <tr>
                    <td className="text-muted">Premium:</td>
                    <td>
                      {userIsPremium ? (
                        <span className="text-warning">
                          <FontAwesomeIcon icon={faCrown} className="me-1" />
                          {userPremiumExpiresAt ?
                            (userPremiumExpiresAt.toDate ? userPremiumExpiresAt.toDate().toLocaleDateString('tr-TR') : new Date(userPremiumExpiresAt).toLocaleDateString('tr-TR')) :
                            'Aktif'}
                        </span>
                      ) : (
                        'Hayır'
                      )}
                    </td>
                  </tr>
                  {userInfo.email && (
                    <tr>
                      <td className="text-muted">Email:</td>
                      <td className="small text-truncate" style={{ maxWidth: '150px' }}>{userInfo.email}</td>
                    </tr>
                  )}
                  {userInfo.phone && (
                    <tr>
                      <td className="text-muted">Telefon:</td>
                      <td className="small">{userInfo.phone}</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Konuşma Notları */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
              <h5 className="mb-3">Konuşma Notları</h5>

              {/* Filtre butonları */}
              <div className="d-flex gap-2 mb-3">
                <Button
                  variant={noteFilter === 'all' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setNoteFilter('all')}
                >
                  Hepsi
                </Button>
                <Button
                  variant={noteFilter === 'member' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setNoteFilter('member')}
                  style={{ backgroundColor: noteFilter === 'member' ? '#6f42c1' : undefined, borderColor: '#6f42c1' }}
                >
                  Üye
                </Button>
                <Button
                  variant={noteFilter === 'bot' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setNoteFilter('bot')}
                  style={{ backgroundColor: noteFilter === 'bot' ? '#28a745' : undefined, borderColor: '#28a745' }}
                >
                  Bot
                </Button>
              </div>

              {/* Manuel not ekleme */}
              <div className="mb-3">
                <div className="d-flex gap-2 mb-2">
                  <div className="d-flex gap-1">
                    <Button
                      variant={noteType === 'bot' ? 'success' : 'outline-success'}
                      size="sm"
                      onClick={() => setNoteType('bot')}
                    >
                      Bot
                    </Button>
                    <Button
                      variant={noteType === 'member' ? 'primary' : 'outline-primary'}
                      size="sm"
                      onClick={() => setNoteType('member')}
                      style={{ backgroundColor: noteType === 'member' ? '#6f42c1' : undefined, borderColor: '#6f42c1' }}
                    >
                      Üye
                    </Button>
                  </div>
                  <Form.Control
                    type="text"
                    placeholder="Not ekle..."
                    value={noteContent}
                    onChange={(e) => setNoteContent(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddManualNote();
                      }
                    }}
                    style={{ flex: 1 }}
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAddManualNote}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </Button>
                </div>
              </div>

              {/* Notlar listesi */}
              <div className="mt-3">
                {conversationNotes
                  .filter(note => noteFilter === 'all' || note.type === noteFilter)
                  .map((note) => (
                    <div
                      key={note.id}
                      className="d-flex align-items-start justify-content-between mb-2 p-2 rounded"
                      style={{
                        backgroundColor: note.type === 'bot' ? '#d4edda' : '#e7d5f8',
                        border: `1px solid ${note.type === 'bot' ? '#28a745' : '#6f42c1'}`
                      }}
                    >
                      <div className="d-flex align-items-start gap-2" style={{ flex: 1, minWidth: 0 }}>
                        <Badge
                          bg={note.type === 'bot' ? 'success' : 'primary'}
                          style={{
                            backgroundColor: note.type === 'bot' ? '#28a745' : '#6f42c1',
                            flexShrink: 0
                          }}
                        >
                          {note.type === 'bot' ? 'Bot' : 'Üye'}
                        </Badge>
                        <span
                          style={{
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            flex: 1,
                            minWidth: 0
                          }}
                        >
                          {note.content}
                        </span>
                      </div>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        onClick={() => handleDeleteNote(note.id)}
                        style={{
                          color: '#dc3545',
                          flexShrink: 0,
                          marginLeft: '8px'
                        }}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </Button>
                    </div>
                  ))}
                {conversationNotes.filter(note => noteFilter === 'all' || note.type === noteFilter).length === 0 && (
                  <p className="text-muted text-center py-3">Henüz not eklenmemiş</p>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modals */}
      <Modal show={showCreditsModal} onHide={() => setShowCreditsModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Jeton Ekle/Sil</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>
              Jeton Değişikliği (Mevcut: {targetUserId === senderId ? userCredits : testUserCredits})
              <br />
              <small className="text-muted">Pozitif değer ekler, negatif değer çıkarır</small>
            </Form.Label>
            <Form.Control
              ref={creditsInputRef}
              type="number"
              value={creditsInput}
              onChange={(e) => setCreditsInput(e.target.value)}
              placeholder="Örn: 100 veya -50"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleUpdateCredits();
                }
              }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreditsModal(false)}>
            İptal
          </Button>
          <Button variant="primary" onClick={handleUpdateCredits}>
            Kaydet
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDiamondModal} onHide={() => setShowDiamondModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Diamond Üye Yap</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Bu üyeyi Diamond üye yapmak istediğinizden emin misiniz?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDiamondModal(false)}>
            İptal
          </Button>
          <Button variant="primary" onClick={handleSetDiamond}>
            Evet, Diamond Yap
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPremiumModal} onHide={() => setShowPremiumModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Premium Yap/Kaldır</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Premium Gün Sayısı (0 = Premium'u Kaldır)</Form.Label>
            <Form.Control
              ref={premiumInputRef}
              type="number"
              value={premiumDaysInput}
              onChange={(e) => setPremiumDaysInput(e.target.value)}
              placeholder="10"
              min="0"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleUpdatePremium();
                }
              }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPremiumModal(false)}>
            İptal
          </Button>
          <Button variant="primary" onClick={handleUpdatePremium}>
            Kaydet
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showBanModal} onHide={() => setShowBanModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Kullanıcıyı Banla</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Bu kullanıcıyı banlamak istediğinizden emin misiniz?
          Banlanan kullanıcılar uygulamaya giriş yapamaz.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBanModal(false)}>
            İptal
          </Button>
          <Button variant="danger" onClick={handleBanUser}>
            Evet, Banla
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCallModal} onHide={() => {
        setShowCallModal(false);
        setCallModalCallerId(null);
        setCallModalReceiverId(null);
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Arama Başlat</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Arama Türü</Form.Label>
            <Form.Select
              value={callType}
              onChange={(e) => setCallType(e.target.value)}
            >
              <option value="normal_voice">Normal Sesli Arama</option>
              <option value="recorded_voice">Sesli Arama (Kayıtlı)</option>
              <option value="recorded_video">Videolu Arama (Kayıtlı)</option>
            </Form.Select>
          </Form.Group>
          {callType === 'normal_voice' && (
            <Alert variant="info">
              Normal sesli arama başlatılacak. Kayıt gerekmez.
            </Alert>
          )}
          {callType === 'recorded_voice' && (
            <Alert variant="info">
              {audioRecordings.length === 0 ? (
                'Ses kaydı bulunamadı. Lütfen önce ses kaydı ekleyin.'
              ) : audioRecordings.length === 1 ? (
                `Kullanılacak kayıt: ${audioRecordings[0].title || 'İsimsiz'}`
              ) : (
                `Toplam ${audioRecordings.length} ses kaydı var. İlk kayıt kullanılacak.`
              )}
            </Alert>
          )}
          {callType === 'recorded_video' && (
            <Alert variant="info">
              {videoRecordings.length === 0 ? (
                'Video kaydı bulunamadı. Lütfen önce video kaydı ekleyin.'
              ) : videoRecordings.length === 1 ? (
                `Kullanılacak kayıt: ${videoRecordings[0].title || 'İsimsiz'}`
              ) : (
                `Toplam ${videoRecordings.length} video kaydı var. İlk kayıt kullanılacak.`
              )}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCallModal(false)}>
            İptal
          </Button>
          <Button variant="primary" onClick={handleStartCall}>
            Aramayı Başlat
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showQuickMessageModal} onHide={() => {
        setShowQuickMessageModal(false);
        setEditingQuickMessage(null);
        setQuickMessageTitle("");
        setQuickMessageContent("");
      }}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingQuickMessage ? 'Hızlı Mesaj Düzenle' : 'Yeni Hızlı Mesaj'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Başlık (Panel İçin)</Form.Label>
            <Form.Control
              type="text"
              value={quickMessageTitle}
              onChange={(e) => setQuickMessageTitle(e.target.value)}
              placeholder="Örn: Selamlaşma"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Mesaj İçeriği</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={quickMessageContent}
              onChange={(e) => setQuickMessageContent(e.target.value)}
              placeholder="Mesaj içeriğini yazın..."
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSaveQuickMessage();
                }
              }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowQuickMessageModal(false);
            setEditingQuickMessage(null);
            setQuickMessageTitle("");
            setQuickMessageContent("");
          }}>
            İptal
          </Button>
          <Button variant="primary" onClick={handleSaveQuickMessage}>
            {editingQuickMessage ? 'Güncelle' : 'Kaydet'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* VIP-Telefon Modal */}
      <Modal show={showVipPhoneModal} onHide={() => setShowVipPhoneModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faStar} className="me-2 text-warning" />
            Paket Mesajı Gönder
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Paket seçiniz</Form.Label>
            <Form.Select
              value={vipPhoneSelectedSku}
              onChange={(e) => setVipPhoneSelectedSku(e.target.value)}
            >
              <option value="">Paket seçiniz...</option>
              {vipPackages.map((pkg) => (
                <option key={pkg.id} value={pkg.sku}>
                  [{pkg.type}] {pkg.packageName} - {pkg.price} TL
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              Lütfen bir paket seçin.
            </Form.Control.Feedback>
            {vipPackages.length === 0 && (
              <Form.Text className="text-muted">
                Henüz paket bulunmuyor. Önce paket ekleyin.
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="mb-0">Mesaj İçeriği</Form.Label>
              <Button
                variant="outline-primary"
                size="xs"
                className="py-0 px-2"
                onClick={() => setShowVipTemplateModal(true)}
              >
                <FontAwesomeIcon icon={faPlus} className="me-1" /> Şablon Ekle
              </Button>
            </div>

            {/* VIP Şablonları */}
            {vipTemplates.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mb-3">
                {vipTemplates.map((template) => (
                  <div key={template.id} className="d-flex align-items-center bg-light border rounded-pill px-3 py-1">
                    <span
                      className="cursor-pointer text-primary"
                      style={{ fontSize: '13px', fontWeight: '500' }}
                      onClick={() => setVipPhoneMessage(template.content)}
                    >
                      {template.title}
                    </span>
                    <FontAwesomeIcon
                      icon={faEdit}
                      className="ms-2 text-muted cursor-pointer"
                      style={{ fontSize: '11px' }}
                      onClick={() => {
                        setEditingVipTemplate(template);
                        setVipTemplateTitle(template.title);
                        setVipTemplateContent(template.content);
                        setShowVipTemplateModal(true);
                      }}
                    />
                    <FontAwesomeIcon
                      icon={faTrash}
                      className="ms-2 text-danger cursor-pointer"
                      style={{ fontSize: '11px' }}
                      onClick={() => handleDeleteVipTemplate(template.id)}
                    />
                  </div>
                ))}
              </div>
            )}

            <Form.Control
              as="textarea"
              rows={4}
              value={vipPhoneMessage}
              onChange={(e) => setVipPhoneMessage(e.target.value)}
              placeholder="Mesaj içeriğini yazın..."
              isInvalid={!vipPhoneMessage.trim()}
            />
            <Form.Control.Feedback type="invalid">
              Mesaj içeriği boş olamaz.
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Bu mesaj kullanıcıya sistem mesajı olarak gönderilecek. Kullanıcı mesaja tıkladığında seçilen VIP paketinin satın alma ekranı açılacak.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVipPhoneModal(false)}>
            İptal
          </Button>
          <Button
            variant="warning"
            onClick={handleSendVipPhoneMessage}
            disabled={!vipPhoneMessage.trim() || !vipPhoneSelectedSku || sending}
          >
            {sending ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="fa-spin me-2" />
                Gönderiliyor...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faStar} className="me-2" />
                Sistem Mesajı Gönder
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Sistem Mesajı Modalı */}
      <Modal show={showSystemMessageModal} onHide={() => setShowSystemMessageModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Sistem Mesajı Gönder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Mesaj İçeriği</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={systemMessageText}
              onChange={(e) => setSystemMessageText(e.target.value)}
              placeholder="Kullanıcıya gönderilecek sistem mesajını yazın..."
            />
            <Form.Text className="text-muted">
              Bu mesaj kullanıcıya "Sistem Mesajı" (sarı kutu) olarak görünecektir.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSystemMessageModal(false)}>
            İptal
          </Button>
          <Button
            variant="primary"
            onClick={handleSendManualSystemMessage}
            disabled={sending || !systemMessageText.trim()}
          >
            {sending ? <FontAwesomeIcon icon={faSpinner} spin className="me-2" /> : null}
            Gönder
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bildirim Gönder Modalı */}
      <Modal show={showNotificationModal} onHide={() => setShowNotificationModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Hazır Bildirim Gönder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Bildirim Başlığı (En az 5 harf)</Form.Label>
            <Form.Control
              type="text"
              value={notificationTitle}
              onChange={(e) => setNotificationTitle(e.target.value)}
              placeholder="Bildirim başlığını giriniz..."
              minLength={5}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Bildirim İçeriği (İsteğe bağlı)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={notificationBody}
              onChange={(e) => setNotificationBody(e.target.value)}
              placeholder="Bildirim açıklamasını giriniz..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNotificationModal(false)} disabled={sendingNotification}>
            İptal
          </Button>
          <Button
            variant="primary"
            onClick={handleSendCustomNotification}
            disabled={sendingNotification || notificationTitle.trim().length < 5}
          >
            {sendingNotification ? <FontAwesomeIcon icon={faSpinner} className="fa-spin me-2" /> : null}
            Gönder
          </Button>
        </Modal.Footer>
      </Modal>

      {/* VIP Şablon Ekle/Düzenle Modalı */}
      <Modal show={showVipTemplateModal} onHide={() => {
        setShowVipTemplateModal(false);
        setEditingVipTemplate(null);
        setVipTemplateTitle("");
        setVipTemplateContent("");
      }}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingVipTemplate ? 'Şablon Düzenle' : 'Yeni Şablon Ekle'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Şablon Başlığı</Form.Label>
            <Form.Control
              type="text"
              value={vipTemplateTitle}
              onChange={(e) => setVipTemplateTitle(e.target.value)}
              placeholder="Örn: VIP Bilgilendirme 1"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Şablon İçeriği</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={vipTemplateContent}
              onChange={(e) => setVipTemplateContent(e.target.value)}
              placeholder="Şablon içeriğini buraya yazın..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowVipTemplateModal(false);
            setEditingVipTemplate(null);
            setVipTemplateTitle("");
            setVipTemplateContent("");
          }}>
            İptal
          </Button>
          <Button variant="primary" onClick={handleSaveVipTemplate}>
            {editingVipTemplate ? 'Güncelle' : 'Kaydet'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
